/**
 * @fileoverview Edit the firm's blocked-caller list on the Admin Phone tab. The backend (`kc-internal-backend/functions/phone_blocked_callers.py`) owns number rules, the firm-line refusal and the known-client check; the phone system reads the saved rows and answers those callers with busy. A saved row does not prove the phone server has reloaded.
 */

import React, { useContext, useEffect, useState } from 'react';

import api from '../../api';
import ToastContext from '../../ToastContext';
import { AppButton, AppTextInput } from '../shared';
import { AdminCard } from './AdminChrome';


function formatPhone(phone) {
  const digits = phone.replace(/\D/g, '').slice(-10);
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function BlockedCallers() {
  const showToast = useContext(ToastContext);
  const [rows, setRows] = useState(null);
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const fail = (summary, error) => showToast({ severity: 'error', summary, detail: error?.response?.data?.error || 'Something went wrong' });

  useEffect(() => {
    let active = true;
    api.get('/admin/blocked-callers')
      .then(({ data }) => active && setRows(data.blocked_callers))
      .catch(() => active && showToast({ severity: 'error', summary: 'Error', detail: 'Blocked numbers could not be loaded' }));
    return () => { active = false; };
  }, [showToast]);

  const block = async (event) => {
    event.preventDefault();
    const save = (confirmClient) => api.post('/admin/blocked-callers', { phone, note, confirm_client: confirmClient });
    setBusy(true);
    try {
      // A known client's number comes back with their names; ask before resending.
      const { data } = await save(false).catch((error) => {
        const clients = error?.response?.data?.clients;
        if (clients && window.confirm(`This number is on file for ${clients.join(', ')}. Block it anyway?`)) return save(true);
        throw error;
      });
      setRows((current) => [data.blocked_caller, ...current]);
      setPhone('');
      setNote('');
    } catch (error) {
      if (!error?.response?.data?.clients) fail('Could not block', error);
    } finally {
      setBusy(false);
    }
  };

  const unblock = async (row) => {
    setBusy(true);
    try {
      await api.delete(`/admin/blocked-callers/${row.id}`);
      setRows((current) => current.filter((item) => item.id !== row.id));
    } catch (error) {
      fail('Could not unblock', error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminCard title="Blocked callers" description="Callers on this list get a busy signal on every firm line.">
      <form className="blocked-callers-form" onSubmit={block}>
        <AppTextInput
          aria-label="Phone number to block"
          placeholder="(555) 555-0100"
          value={phone}
          onChange={(event) => setPhone(event.target.value)} />
        <AppTextInput
          aria-label="Reason"
          placeholder="Reason (optional)"
          maxLength={500}
          value={note}
          onChange={(event) => setNote(event.target.value)} />
        {/* Blocking waits for the list so a late load can't overwrite a number added meanwhile. */}
        <AppButton type="submit" icon="pi pi-ban" label="Block number" disabled={busy || rows === null || !phone.trim()} />
      </form>

      {rows?.length === 0 ? <p className="blocked-callers-empty">No numbers are blocked.</p> : null}
      {rows?.length ? (
        <ul className="blocked-callers-list" aria-label="Blocked numbers">
          {rows.map((row) => (
            <li key={row.id} className="blocked-callers-row">
              <span className="blocked-callers-row__phone">{formatPhone(row.phone)}</span>
              <span className="blocked-callers-row__note">{row.note}</span>
              <AppButton
                variant="secondary"
                label="Unblock"
                aria-label={`Unblock ${formatPhone(row.phone)}`}
                disabled={busy}
                onClick={() => unblock(row)} />
            </li>
          ))}
        </ul>
      ) : null}
    </AdminCard>
  );
}

export default BlockedCallers;
